/**
 * Full self-paced lesson content for the Solar Black Belt path.
 * Educational only — each lesson ends with a soft, optional next step.
 */
export const LESSONS = [
  {
    name: "White belt",
    title: "Know your bill",
    minutes: "About 8 minutes",
    intro:
      "Every good solar decision starts with the bill, not the panels. This lesson walks through what a commercial utility bill actually says, so the numbers stop being a wall of jargon. Nothing here requires a sale, an account, or a commitment.",
    sections: [
      {
        h: "The parts of a commercial bill",
        p: [
          "Most commercial bills combine four things: energy charges (your total kWh used), demand charges (your single highest power draw, billed in kW), fixed customer charges, and riders or taxes. Energy gets the attention, but for many facilities demand and related charges are a third to half of the total.",
          "That split matters because solar and storage attack different parts. Solar reduces the energy you buy from the grid. Batteries can flatten the short peaks that set your demand charge. If you only look at kWh, you only see part of the opportunity.",
        ],
      },
      {
        h: "kW versus kWh",
        p: [
          "A kilowatt-hour (kWh) is a quantity of energy — like the miles you drove this month. A kilowatt (kW) is a rate — your top speed at one moment. Utilities bill large customers for both: the miles and the fastest hill.",
          "A useful habit: when you read a bill, find both numbers. Total kWh tells you how much energy solar could offset. Peak kW tells you whether demand charges — and therefore storage — are worth studying for your building.",
        ],
      },
      {
        h: "Rate schedules, time-of-use and ratchets",
        p: [
          "Somewhere on the bill is a rate schedule name (often a code like GS-2 or TOU-8). That code decides how you are charged: flat rates, time-of-use windows where power costs more in the afternoon, and demand ratchets that can bill a peak set months ago.",
          "You do not need to master the tariff. You only need to know the schedule name and where your usage shows up — that is enough for any reviewer to pull the actual rules.",
        ],
      },
    ],
    terms: [
      ["Energy (kWh)", "The total electricity you used — the “miles driven” on your bill."],
      ["Demand (kW)", "Your highest rate of draw in the billing window — the “top speed” utilities bill separately."],
      ["Time-of-use (TOU)", "A tariff structure where power costs more in certain windows, usually afternoons."],
      ["Ratchet", "A rule that can bill demand based on a past peak, not just the current month."],
    ],
    tryThis: [
      "Pull your last three bills (PDFs or photos are fine).",
      "Circle the total kWh on each one.",
      "Write down the rate schedule name and any kW or demand line you can find.",
    ],
    nextStep: "Whenever you're ready, the next lesson looks at the building itself. No rush — the path stays here as long as you need it.",
  },
  {
    name: "Yellow belt",
    title: "Understand your roof",
    minutes: "About 10 minutes",
    intro:
      "The roof decides what is physically possible. This lesson covers what makes a roof solar-ready, how orientation and shade shape a layout, and the honest rule-of-thumb for turning square feet into kilowatts. Photographs and estimates are a starting point — never a structural survey.",
    sections: [
      {
        h: "What makes a roof solar-ready",
        p: [
          "Three questions come first. How old is the roof? Solar equipment is commonly expected to run 25 years or more, so a roof near the end of its life is usually replaced before (or alongside) the project. What is the structure? Older decks and long spans affect what racking is allowed. And who controls it? Tenants need the owner's permission before anything else happens.",
          "None of these questions disqualify a building — they just set the order of operations.",
        ],
      },
      {
        h: "Orientation, pitch and shade",
        p: [
          "Panels produce the most energy when they face the sun. In the northern hemisphere that generally means south-facing planes, with east and west adding useful morning and afternoon output. Pitch matters less than people expect; shade matters more. Rooftop units, vents, parapets and neighboring trees all carve holes in a usable layout.",
          "This is why a photo review is worth ten minutes: obstructions are the difference between a tidy layout and a redesign.",
        ],
      },
      {
        h: "From square feet to kilowatts",
        p: [
          "A working rule of thumb for flat commercial roofs: about 5 to 8 watts of solar per usable square foot once obstructions and walkways are subtracted. A 30,000 sq ft roof might support roughly 150 to 240 kW — a wide range, which is exactly why real layouts beat estimates.",
          "Treat any per-square-foot estimate as a sizing assumption to be confirmed by an engineer, not a promise.",
        ],
      },
    ],
    terms: [
      ["Azimuth", "The compass direction a roof face points; south-facing is usually strongest."],
      ["Usable area", "Roof space left after subtracting equipment, setbacks and walkways."],
      ["Watts per square foot", "A sizing rule of thumb linking roof area to system capacity."],
      ["Structural review", "The engineering check that confirms the roof can carry the array."],
    ],
    tryThis: [
      "Estimate your roof's age and note the surface type.",
      "Photograph each roof face from the ground or a window.",
      "List obvious obstructions: HVAC units, vents, skylights, shade from taller neighbors.",
    ],
    nextStep: "Next, whenever you like: what the equipment actually does, and where batteries fit.",
  },
  {
    name: "Green belt",
    title: "Connect solar + storage",
    minutes: "About 10 minutes",
    intro:
      "Solar produces energy; batteries move it in time. Understanding what each piece can and cannot do keeps expectations honest — especially around outages, peak charges, and what “backup” really means for a commercial building.",
    sections: [
      {
        h: "What solar does",
        p: [
          "Solar turns daytime sunlight into electricity your building consumes first. Every kilowatt-hour you generate and self-consume is a kilowatt-hour you do not buy. Under time-of-use rates, afternoon solar lines up well with expensive windows.",
          "Most commercial solar stays grid-connected. When the grid goes down, standard systems pause for safety — which brings us to storage.",
        ],
      },
      {
        h: "What storage does",
        p: [
          "A battery does two distinct jobs. Economically, it can shave the short peaks that set your demand charge — discharging for the minutes your facility spikes. Operationally, a correctly configured system can support selected loads during an outage.",
          "Power and energy capacity are different sizing questions: how much at once (kW) versus how long for (kWh). List what must stay on in an outage, not the whole building — the answer usually fits a smaller, cheaper system than people assume.",
        ],
      },
      {
        h: "How it connects",
        p: [
          "Everything ties through your electrical service and the utility's interconnection process. Service panel size, switchgear location and the utility's review timeline are real project variables. This is also where interval data — your utility's 15-minute usage export — becomes valuable, because it shows exactly when your peaks happen.",
          "You can request that export from your utility's portal; it is usually free and takes a few days.",
        ],
      },
    ],
    terms: [
      ["Self-consumption", "The share of solar generation used in the building rather than exported."],
      ["Peak shaving", "Using stored energy to flatten short demand spikes."],
      ["Backup loads", "The circuits or equipment chosen to keep running during an outage."],
      ["Interconnection", "The utility's approval process for connecting a system to the grid."],
    ],
    tryThis: [
      "List the loads that must stay on in an outage — refrigeration, servers, a gate, lighting.",
      "Note your electrical service size from the main panel label.",
      "Mark where battery cabinets could physically live.",
    ],
    nextStep: "When you're ready, the brown belt lesson is the money lesson — cost, financing and incentives.",
  },
  {
    name: "Brown belt",
    title: "Compare the economics",
    minutes: "About 12 minutes",
    intro:
      "This is the lesson most websites skip. Commercial solar is a capital project with a small number of big questions: what it costs, who owns it, who claims the incentives, and when the numbers turn. Learn the shape of the answer before anyone asks you to sign anything.",
    sections: [
      {
        h: "What drives cost",
        p: [
          "Commercial systems are usually priced in dollars per watt of capacity, with batteries priced separately, plus ongoing costs for monitoring, insurance and upkeep. Scale helps: larger roofs spread fixed costs over more watts, which is why commercial projects often reach numbers residential ones cannot.",
          "Any per-watt number you see online is an assumption until it is quoted against your actual building and utility.",
        ],
      },
      {
        h: "The three capital paths",
        p: [
          "Ownership (cash or financed) means you own the system and keep the savings and any incentives. C-PACE is a structure where qualifying energy projects are repaid through a property assessment. A power purchase agreement (PPA) means a developer owns the system on your roof and you buy its power at a agreed rate — no upfront cost, but the tax benefits belong to the owner.",
          "None of the three is universally best. The honest comparison puts all paths on the same inputs and shows the trade-offs side by side.",
        ],
      },
      {
        h: "Incentives and taxes — handled with care",
        p: [
          "Federal and state incentives can materially change commercial solar economics, but eligibility, timing, qualified basis and the ability to actually use credits or deductions are tax questions. Rules change; federal and state rules do not simply stack.",
          "The safe pattern: treat every incentive as a scenario until your tax adviser confirms it in writing. A website that assumes you qualify before asking is not doing you a favor.",
        ],
      },
      {
        h: "Payback and beyond",
        p: [
          "Simple payback — net cost divided by annual after-tax savings — is the number people quote, and it is a blunt instrument. It ignores degradation, utility escalation, replacements and the time value of money. It is a useful first filter, not a return analysis.",
          "When you compare paths, ask for the assumptions list. If a proposal will not show its assumptions, that tells you something.",
        ],
      },
    ],
    terms: [
      ["Dollars per watt ($/W)", "The standard way system cost is expressed before project specifics."],
      ["C-PACE", "Financing repaid through a property assessment for qualifying energy projects."],
      ["PPA", "A contract to buy power from a system owned by someone else on your roof."],
      ["Simple payback", "Net cost divided by annual after-tax savings — a first filter, not a full return analysis."],
    ],
    tryThis: [
      "Run the calculator with your own spend and roof numbers.",
      "Ask your tax adviser about credit and deduction eligibility for your entity.",
      "Compare ownership, C-PACE and PPA on the same inputs before ranking them.",
    ],
    nextStep: "The final lesson is short: how to organize what you've learned into a decision.",
  },
  {
    name: "Black belt",
    title: "Plan your next move",
    minutes: "About 6 minutes",
    intro:
      "You now know the bill, the roof, the equipment and the economics. The last step is assembly: putting your information in one place so any review — ours or a competitor's — starts from facts instead of guesses.",
    sections: [
      {
        h: "Build your dossier",
        p: [
          "The complete starter kit is short: up to twelve recent bills, your utility's interval-data export if demand matters to you, roof age and photo notes from the yellow belt lesson, and your priority list — savings, resilience, sustainability, or a mix.",
          "Keep it in one folder. Everything a serious reviewer asks for is now in your hands.",
        ],
      },
      {
        h: "What a real assessment covers",
        p: [
          "A proper project assessment checks engineering (layout, structure, electrical), interconnection with your utility, financial scenarios with disclosed assumptions, and a timeline that names who does what. It ends with a proposal you can interrogate — because you know what the numbers mean now.",
          "If an assessment skips the bill, the tariff, or the assumptions, treat the result as a rough sketch.",
        ],
      },
      {
        h: "Reading a proposal",
        p: [
          "Check five things: the sizing assumptions, the tariff assumptions, the incentive treatment (scenario or confirmed?), the operations and maintenance story, and what is explicitly not guaranteed. Honest proposals flag their own uncertainty.",
          "You do not need to be an engineer to ask those five questions — you just need to ask them.",
        ],
      },
    ],
    terms: [
      ["Interval data", "Your utility's detailed usage export, usually in 15-minute increments."],
      ["Interconnection review", "The utility's technical and timing approval for your specific system."],
      ["Disclosed assumptions", "The listed inputs behind any projected number — the mark of an honest proposal."],
    ],
    tryThis: [
      "Gather your bills and notes into one folder.",
      "Write down your top two priorities for the project.",
      "Prepare the five proposal questions above.",
    ],
    nextStep:
      "That's the whole path — self-paced, no badges required. Whenever it's useful, we're happy to review your bill and give you a grounded second opinion; the upload button appears when you want it, not before.",
  },
];
